import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";
import { useContext, useEffect } from "react";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

export const UserTrace = () => {
    const user = useContext(UserInfoContext);
    useEffect(() => {
        if (user?.globalUserId) {
            // datadogRum.init() runs pre-render in index-app.tsx off the init-context endpoint.
            // Ask the SDK whether it ran rather than re-deriving "enabled" from a second config source.
            if (datadogRum.getInitConfiguration()) {
                datadogRum.setUser({
                    name: (user.fullName ?? "").trim(),
                    id: user.globalUserId.toString(),
                    email: firstEmailFromList(user.email) ?? "",
                    orgs: user.orgLinks?.map((x) => x.orgId),
                    orgLinkIds: user.orgLinks?.map((x) => x.orgLinkId),
                });
            }

            const scope = Sentry.getCurrentScope();
            // eslint-disable-next-line deprecate/member-expression
            const atTestContextCookie = Cookies.get("ATTestContext");
            if (atTestContextCookie) {
                try {
                    scope.setContext("Automated Testing", JSON.parse(atTestContextCookie));
                } catch (e) {}
            }
            scope.setUser({
                id: user?.globalUserId,
                username: user?.userName ?? undefined,
                email: firstEmailFromList(user.email) ?? undefined,
                orgLinkIds: user.orgLinks?.map((x) => x.orgLinkId),
                orgs: user.orgLinks?.map((x) => x.orgId),
            });
            scope.setTag(
                "builder-id",
                user?.orgLinks?.find((x) => x.orgLinkId === user?.globalUserId)?.orgId,
            );
        }
    }, [user?.email, user?.fullName, user?.globalUserId, user?.orgLinks, user?.userName]);
    return <></>;
};

/**
 * Returns the first email address found in a semicolon-separated recipient list.
 * Handles entries like:
 *   John Doe <john@example.com>; "Smith, Jane" <jane@foo.co.uk>; Alias Only; other@bar.io
 * If no email is found, returns null.
 */
function firstEmailFromList(list: string | null | undefined): string | null {
    if (!list) return null;

    // Split on semicolons; Outlook/clients use ';' as the separator.
    const parts = list
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

    // Simple but resilient email matcher (covers common real-world cases).
    // Looks for a bare address or one inside <...>.
    const EMAIL_RE =
        /(?:^|[\s<'"(])([A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)(?=[\s>'"),;]|$)/;

    for (const part of parts) {
        const m = EMAIL_RE.exec(part);
        if (m) {
            // normalize casing (domains aren’t case sensitive; keeps it tidy)
            return m[1].toLowerCase();
        }
    }
    return null;
}
